/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type { ReactNode } from 'react';
import type { Id } from 'react-toastify';

export type RetainedToastType = 'error' | 'warning';

export interface RetainedToast {
  id: Id;
  type: RetainedToastType;
  content: ReactNode;
  summary: string;
  minimized: boolean;
}

interface ToastHistorySnapshot {
  records: ReadonlyArray<RetainedToast>;
  detailsId: Id | null;
}

type Listener = () => void;

const MAX_RETAINED_TOASTS = 10;
let snapshot: ToastHistorySnapshot = { records: [], detailsId: null };
const listeners = new Set<Listener>();

function updateSnapshot(next: ToastHistorySnapshot): void {
  snapshot = next;
  listeners.forEach((listener) => {
    listener();
  });
}

export function retainToast(toast: Omit<RetainedToast, 'minimized'>): void {
  const records = [
    ...snapshot.records.filter(({ id }) => id !== toast.id),
    { ...toast, minimized: false },
  ].slice(-MAX_RETAINED_TOASTS);
  updateSnapshot({
    records,
    detailsId: records.some(({ id }) => id === snapshot.detailsId) ? snapshot.detailsId : null,
  });
}

export function minimizeRetainedToast(id: Id): void {
  if (!snapshot.records.some((toast) => toast.id === id)) return;
  updateSnapshot({
    ...snapshot,
    records: snapshot.records.map((toast) =>
      toast.id === id ? { ...toast, minimized: true } : toast,
    ),
  });
}

export function dismissRetainedToast(id: Id): void {
  if (!snapshot.records.some((toast) => toast.id === id)) return;
  updateSnapshot({
    records: snapshot.records.filter((toast) => toast.id !== id),
    detailsId: snapshot.detailsId === id ? null : snapshot.detailsId,
  });
}

export function openRetainedToastDetails(id: Id): void {
  if (!snapshot.records.some((toast) => toast.id === id)) return;
  updateSnapshot({ ...snapshot, detailsId: id });
}

export function closeRetainedToastDetails(): void {
  if (snapshot.detailsId === null) return;
  updateSnapshot({ ...snapshot, detailsId: null });
}

export function clearToastHistory(): void {
  if (snapshot.records.length === 0 && snapshot.detailsId === null) return;
  updateSnapshot({ records: [], detailsId: null });
}

export function subscribeToToastHistory(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getToastHistorySnapshot(): ToastHistorySnapshot {
  return snapshot;
}
